import React, { useEffect, useState } from "react";
import { Drawer, Timeline, Empty, Pagination, Spin, Tag, Tooltip, notification } from "antd";
import { HistoryOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import styled from "styled-components";

import { ancanaApi } from "../api/ancanaApi";

interface ChangeLogChangedBy {
  id: number;
  full_name: string;
  email: string;
}

interface ChangeLogEntry {
  id: number;
  changes: { [fieldName: string]: { new: any; old: any } };
  changed_by: ChangeLogChangedBy | null;
  note: string;
  created_at: string;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface ChangeLogDrawerProps {
  recordPath: string;
  recordId: number | string;
  recordName?: string;
  open: boolean;
  onClose: () => void;
  width?: number | string;
  fieldLabels?: { [fieldName: string]: string };
}

const PAGE_SIZE = 25;

const EntryCard = styled.div`
  border: 1px solid ${({ theme }) => theme?.borderColor || '#eee'};
  border-radius: 6px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.02);
`;

const ChangeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 2px;

  .field-name {
    font-weight: 600;
    text-transform: capitalize;
  }

  .value-old {
    color: #b34646;
    background: rgba(248, 89, 89, 0.08);
    padding: 0 4px;
    border-radius: 3px;
    text-decoration: line-through;
    word-break: break-word;
  }

  .value-new {
    color: #1f7a1f;
    background: rgba(38, 179, 38, 0.1);
    padding: 0 4px;
    border-radius: 3px;
    word-break: break-word;
  }

  .arrow {
    color: #999;
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #777;
  margin-bottom: 6px;
  gap: 8px;
`;

const formatFieldName = (name: string) =>
  name.replaceAll('_', ' ');

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <em style={{ opacity: 0.6 }}>empty</em>;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <em style={{ opacity: 0.6 }}>[]</em>;
    return `[${value.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ')}]`;
  }
  if (typeof value === 'object') {
    return <code style={{ fontSize: 11 }}>{JSON.stringify(value)}</code>;
  }
  return String(value);
};

function ChangeLogDrawer(props: ChangeLogDrawerProps) {
  const { recordPath, recordId, recordName, open, onClose, width = 520, fieldLabels } = props;
  const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchChangeLog = (targetPage: number) => {
    setIsLoading(true);
    ancanaApi
      .get<PaginatedResponse<ChangeLogEntry>>(
        `${recordPath}/${recordId}/change-log/?page=${targetPage}&page_size=${PAGE_SIZE}`
      )
      .then(res => {
        setEntries(res.data.results);
        setTotal(res.data.count);
      })
      .catch(e => {
        notification.error({
          message: 'Failed to load change log',
          description: e.message,
          placement: 'bottomRight',
        });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchChangeLog(1);
    }
  }, [open, recordId, recordPath]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchChangeLog(newPage);
  };

  const labelFor = (fieldName: string) =>
    fieldLabels?.[fieldName] || formatFieldName(fieldName);

  return (
    <Drawer
      title={
        <div className="disp-flex h-stack-strict center gap-xs">
          <HistoryOutlined />
          <span>Change log{recordName ? ` — ${recordName}` : ''}</span>
        </div>
      }
      placement="right"
      width={width}
      onClose={onClose}
      visible={open}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        {entries.length === 0 && !isLoading ? (
          <Empty description="No changes recorded yet" />
        ) : (
          <Timeline>
            {entries.map(entry => (
              <Timeline.Item key={entry.id} color="blue">
                <Meta>
                  <span>
                    <UserOutlined />{' '}
                    <strong>
                      {entry.changed_by?.full_name || entry.changed_by?.email || 'System'}
                    </strong>
                  </span>
                  <Tooltip title={moment(entry.created_at).format('LLLL')}>
                    <span>{moment(entry.created_at).fromNow()}</span>
                  </Tooltip>
                </Meta>

                <EntryCard>
                  {Object.entries(entry.changes).map(([fieldName, diff]) => (
                    <ChangeRow key={fieldName}>
                      <span className="field-name">{labelFor(fieldName)}:</span>
                      <span className="value-old">{renderValue(diff.old)}</span>
                      <span className="arrow">→</span>
                      <span className="value-new">{renderValue(diff.new)}</span>
                    </ChangeRow>
                  ))}

                  {entry.note && (
                    <div style={{ marginTop: 8 }}>
                      <Tag color="default" style={{ whiteSpace: 'normal' }}>
                        {entry.note}
                      </Tag>
                    </div>
                  )}
                </EntryCard>
              </Timeline.Item>
            ))}
          </Timeline>
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Pagination
              size="small"
              current={page}
              pageSize={PAGE_SIZE}
              total={total}
              showSizeChanger={false}
              onChange={handlePageChange}
            />
          </div>
        )}
      </Spin>
    </Drawer>
  );
}

export default ChangeLogDrawer;
